import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  Plus,
  Edit2,
  Search,
  Database,
  X,
  ChevronDown
} from 'lucide-react';
import { SystemAdminConfigAPI } from '../../../services/api/SystemAdminConfigAPI';

const SystemConfiguration = () => {
  const [configurations, setConfigurations] = useState([]);

  const [searchLoading, setSearchLoading] = useState(false);
  
  // Hierarchical selection state
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  
  // Available options for dropdowns
  const [availableModules, setAvailableModules] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  // Form data for adding/editing
  const [formData, setFormData] = useState({
    module: '',
    category: '',
    subcategory: '',
    key: '',
    value: '',
    encrypted: false,
    description: ''
  });

  // Load modules on component mount
  useEffect(() => {
    loadModules();
  }, []);

  // Load categories when module changes
  useEffect(() => {
    if (selectedModule) {
      loadCategories(selectedModule);
      setSelectedCategory('');
      setSelectedSubcategory('');
      setConfigurations([]);
    } else {
      setAvailableCategories([]);
      setAvailableSubcategories([]);
      setSelectedCategory('');
      setSelectedSubcategory('');
      setConfigurations([]);
    }
  }, [selectedModule]);

  // Load subcategories when category changes
  useEffect(() => {
    if (selectedModule && selectedCategory) {
      loadSubcategories(selectedModule, selectedCategory);
      setSelectedSubcategory('');
      setConfigurations([]);
    } else {
      setAvailableSubcategories([]);
      setSelectedSubcategory('');
      setConfigurations([]);
    }
  }, [selectedCategory]);

  // Clear configurations when subcategory changes
  useEffect(() => {
    if (!selectedSubcategory) {
      setConfigurations([]);
    }
  }, [selectedSubcategory]);

  const loadModules = async () => {
    try {
      const data = await SystemAdminConfigAPI.getModules();
      setAvailableModules(data.modules || []);
    } catch (error) {
      console.error('Error loading modules:', error);
      toast.error('Failed to load modules');
    }
  };

  const loadCategories = async (module) => {
    try {
      const data = await SystemAdminConfigAPI.getCategories({ module });
      setAvailableCategories(data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const loadSubcategories = async (module, category) => {
    try {
      const data = await SystemAdminConfigAPI.getSubcategories({ module, category });
      setAvailableSubcategories(data.subcategories || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
      toast.error('Failed to load subcategories');
    }
  };

  const handleSearch = async () => {
    if (!selectedModule || !selectedCategory || !selectedSubcategory) {
      toast.error('Please select Module, Category, and Subcategory');
      return;
    }

    try {
      setSearchLoading(true);
      const filters = {
        module: getModuleName(selectedModule),
        category: getCategoryName(selectedCategory),
        subcategory: getSubcategoryName(selectedSubcategory)
      };
      const data = await SystemAdminConfigAPI.getAllConfigs(filters);
      setConfigurations(data);
    } catch (error) {
      toast.error('Failed to load configurations');
      console.error('Error loading configurations:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddConfiguration = async () => {
    try {
      const configData = {
        ...formData,
        module: getModuleName(selectedModule),
        category: getCategoryName(selectedCategory),
        subcategory: getSubcategoryName(selectedSubcategory)
      };
      await SystemAdminConfigAPI.createConfig(configData);
      toast.success('Configuration created successfully');
      setShowAddModal(false);
      setFormData({
        module: '',
        category: '',
        subcategory: '',
        key: '',
        value: '',
        encrypted: false,
        description: ''
      });
      // Refresh the search results
      handleSearch();
    } catch (error) {
      toast.error('Failed to create configuration');
      console.error('Error creating configuration:', error);
    }
  };

  const handleEditConfiguration = async () => {
    try {
      await SystemAdminConfigAPI.updateConfig(editingConfig.id, {
        value: formData.value,
        description: formData.description
      });
      toast.success('Configuration updated successfully');
      setEditingConfig(null);
      setFormData({
        module: '',
        category: '',
        subcategory: '',
        key: '',
        value: '',
        encrypted: false,
        description: ''
      });
      // Refresh the search results
      handleSearch();
    } catch (error) {
      toast.error('Failed to update configuration');
      console.error('Error updating configuration:', error);
    }
  };

  const handleDeleteConfiguration = async (configId) => {
    try {
      await SystemAdminConfigAPI.deleteConfig(configId);
      toast.success('Configuration deleted successfully');
      setShowDeleteModal(null);
      // Refresh the search results
      handleSearch();
    } catch (error) {
      toast.error('Failed to delete configuration');
      console.error('Error deleting configuration:', error);
    }
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormData({
      module: config.module,
      category: config.category,
      subcategory: config.subcategory,
      key: config.key,
      value: config.encrypted ? '' : config.value,
      encrypted: config.encrypted,
      description: config.description || ''
    });
  };

  const openAddModal = () => {
    if (!selectedModule || !selectedCategory || !selectedSubcategory) {
      toast.error('Please select Module, Category, and Subcategory first');
      return;
    }
    setFormData({
      module: selectedModule,
      category: selectedCategory,
      subcategory: selectedSubcategory,
      key: '',
      value: '',
      encrypted: false,
      description: ''
    });
    setShowAddModal(true);
  };

  const resetSelection = () => {
    setSelectedModule('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setConfigurations([]);
    setAvailableCategories([]);
    setAvailableSubcategories([]);
  };

  // Helper functions to get names from IDs
  const getModuleName = (id) => availableModules.find(m => m.id === id)?.name || '';
  const getCategoryName = (id) => availableCategories.find(c => c.id === id)?.name || '';
  const getSubcategoryName = (id) => availableSubcategories.find(s => s.id === id)?.name || '';

  // Check if search should be enabled
  const canSearch = selectedModule && selectedCategory && selectedSubcategory;

  const ConfigModal = ({ isOpen, onClose, title, onSave, isEdit = false }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            {!isEdit && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
                  <input
                    type="text"
                    value={formData.module}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
              <input
                type="text"
                value={formData.key}
                onChange={(e) => setFormData({...formData, key: e.target.value})}
                disabled={isEdit}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5] ${isEdit ? 'bg-gray-100' : ''}`}
                placeholder="USERNAME, PASSWORD, HOST, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
              <textarea
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                rows="3"
                placeholder={formData.encrypted ? "Enter new value (leave empty to keep current)" : "Configuration value"}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                rows="2"
                placeholder="Description of this configuration"
              />
            </div>

            {!isEdit && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="encrypted"
                  checked={formData.encrypted}
                  onChange={(e) => setFormData({...formData, encrypted: e.target.checked})}
                  className="h-4 w-4 text-[#207CE5] focus:ring-[#207CE5] border-gray-300 rounded"
                />
                <label htmlFor="encrypted" className="ml-2 block text-sm text-gray-900">
                  Encrypted (for sensitive values)
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C]"
            >
              {isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const DeleteModal = ({ config, onClose, onConfirm }) => {
    if (!config) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              <X className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Configuration</h3>
              <p className="text-sm text-gray-500">This action cannot be undone.</p>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete this configuration?
            </p>
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-900">
                {config.module}.{config.category}.{config.subcategory}.{config.key}
              </p>
              {config.description && (
                <p className="text-sm text-gray-600 mt-1">{config.description}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(config.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Configuration</h1>
          <p className="text-gray-600">Manage system settings and configuration values</p>
        </div>
      </div>

      {/* Hierarchical Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Select Configuration Path</h3>
                      <button
              onClick={resetSelection}
              className="text-sm text-[#207CE5] hover:text-[#0B1C2C]"
            >
            Reset Selection
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Module Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Module <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5] appearance-none"
              >
                <option value="">Select Module</option>
                {Array.isArray(availableModules) && availableModules.map(module => (
                  <option key={module.id || module} value={module.id || module}>{module.name || module}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={!selectedModule}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5] appearance-none disabled:bg-gray-100"
              >
                <option value="">Select Category</option>
                {Array.isArray(availableCategories) && availableCategories.map(category => (
                  <option key={category.id || category} value={category.id || category}>{category.name || category}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          {/* Subcategory Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategory <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                disabled={!selectedCategory}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5] appearance-none disabled:bg-gray-100"
              >
                <option value="">Select Subcategory</option>
                {Array.isArray(availableSubcategories) && availableSubcategories.map(subcategory => (
                  <option key={subcategory.id || subcategory} value={subcategory.id || subcategory}>{subcategory.name || subcategory}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Search Button */}
        {canSearch && (
          <div className="flex justify-center">
            <button
              onClick={handleSearch}
              disabled={searchLoading}
              className="flex items-center space-x-2 px-6 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C] disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              <span>{searchLoading ? 'Searching...' : 'Search'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Results Section */}
      {canSearch && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Configuration Keys
              </h3>
              <p className="text-sm text-gray-600">
                {getModuleName(selectedModule)} → {getCategoryName(selectedCategory)} → {getSubcategoryName(selectedSubcategory)}
              </p>
            </div>
            <button
              onClick={openAddModal}
              className="flex items-center space-x-2 px-4 py-2 bg-[#008080] text-white rounded-lg hover:bg-[#0B1C2C]"
            >
              <Plus className="h-4 w-4" />
              <span>Add Key</span>
            </button>
          </div>

          {searchLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207CE5] mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading configurations...</p>
            </div>
          ) : configurations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No configuration keys found for this path</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Key
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modified
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(configurations) && configurations.map((config) => (
                    <tr key={config.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 font-mono">
                          {config.key}
                        </div>
                        {config.encrypted && (
                          <div className="text-xs text-yellow-600">🔒 Encrypted</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-mono max-w-xs truncate">
                          {config.encrypted ? '[ENCRYPTED]' : (config.value || '(empty)')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {config.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(config.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(config)}
                            className="text-[#207CE5] hover:text-[#0B1C2C]"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setShowDeleteModal(config)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Configuration Modal */}
      <ConfigModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Configuration Key"
        onSave={handleAddConfiguration}
        isEdit={false}
      />

      {/* Edit Configuration Modal */}
      <ConfigModal
        isOpen={!!editingConfig}
        onClose={() => setEditingConfig(null)}
        title="Edit Configuration"
        onSave={handleEditConfiguration}
        isEdit={true}
      />

      {/* Delete Configuration Modal */}
      <DeleteModal
        config={showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        onConfirm={handleDeleteConfiguration}
      />
    </div>
  );
};

export default SystemConfiguration;